/**
 * FOS Cluster Legend
 *
 * Displays cluster labels with color coding for the embedding visualization.
 * Supports:
 * - Click to select/filter by cluster
 * - Hover to highlight cluster in canvas (dims other clusters)
 * - Outcome/cluster color mode switching
 *
 * Cluster info is now displayed on the canvas itself when hovering,
 * providing a more cohesive visualization experience.
 *
 * Uses GO brand colors via OutcomeBadge:
 * - Berry → Upheld (risk to firm)
 * - Teal → Not Upheld (favorable)
 * - Purple → Partially Upheld (needs review)
 */

import React from 'react';
import type { FOSClusterInfo } from '../../../utils/api';
import { ScrollArea } from '../../ui/scroll-area';
import { OutcomeBadge, type OutcomeType } from '../../ui/outcome-badge';
import { cn } from '../../../lib/utils';

// Cluster colors using GO brand palette
const CLUSTER_COLORS = [
  '#0003AE', // sapphire
  '#924EFF', // purple
  '#07A0C3', // teal
  '#92204D', // berry
  '#FFA9E7', // pink
  '#80D5FF', // sky
  '#500091', // indigo
  '#4342FF', // primary
  '#F59E0B', // amber
  '#64748b', // slate
];

interface FOSClusterLegendProps {
  clusters: FOSClusterInfo[];
  colorMode: 'outcome' | 'cluster';
  selectedClusterId?: number | null;
  hoveredClusterId?: number | null;
  onClusterClick?: (clusterId: number | null) => void;
  onClusterHover?: (clusterId: number | null) => void;
  className?: string;
}

const FOSClusterLegend: React.FC<FOSClusterLegendProps> = ({
  clusters,
  colorMode,
  selectedClusterId,
  hoveredClusterId,
  onClusterClick,
  onClusterHover,
  className,
}) => {
  if (colorMode === 'outcome') {
    // Show outcome legend with semantic GO brand colors
    const outcomes: OutcomeType[] = ['upheld', 'not_upheld', 'partially_upheld'];

    return (
      <div className={cn('space-y-3', className)}>
        <h4 className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
          Outcome
        </h4>
        <div className="space-y-2">
          {outcomes.map((outcome) => (
            <div key={outcome} className="flex items-center justify-between">
              <OutcomeBadge outcome={outcome} size="sm" />
            </div>
          ))}
        </div>
      </div>
    );
  }

  // Show cluster legend
  return (
    <div className={cn('space-y-2', className)}>
      <h4 className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
        Clusters ({clusters.length})
      </h4>
      <ScrollArea className="h-[300px]">
        <div className="space-y-0.5 pr-3">
          {/* Show all clusters option */}
          <button
            onClick={() => onClusterClick?.(null)}
            onMouseEnter={() => onClusterHover?.(null)}
            onMouseLeave={() => onClusterHover?.(null)}
            className={cn(
              'w-full text-left px-2 py-1.5 rounded-md text-sm transition-all duration-150',
              selectedClusterId === null
                ? 'bg-primary/10 text-primary font-medium'
                : 'hover:bg-muted/70'
            )}
          >
            <div className="flex items-center gap-2.5">
              <div className="w-2.5 h-2.5 rounded-full bg-gray-400" />
              <span>All Clusters</span>
            </div>
          </button>

          {/* Individual clusters */}
          {clusters.map((cluster) => {
            const color = CLUSTER_COLORS[cluster.cluster_num % CLUSTER_COLORS.length];
            const isSelected = selectedClusterId === cluster.cluster_num;
            const isHovered = hoveredClusterId === cluster.cluster_num;
            const isDimmed = hoveredClusterId !== null && !isHovered;

            return (
              <button
                key={cluster.cluster_num}
                onClick={() => onClusterClick?.(cluster.cluster_num)}
                onMouseEnter={() => onClusterHover?.(cluster.cluster_num)}
                onMouseLeave={() => onClusterHover?.(null)}
                className={cn(
                  'w-full text-left px-2 py-1.5 rounded-md transition-all duration-150',
                  isSelected && 'bg-primary/10',
                  isHovered && !isSelected && 'bg-muted/80',
                  !isSelected && !isHovered && 'hover:bg-muted/50',
                  isDimmed && 'opacity-40'
                )}
              >
                <div className="flex items-center gap-2.5">
                  <div
                    className={cn(
                      'rounded-full flex-shrink-0 transition-all duration-150',
                      isHovered ? 'w-3 h-3' : 'w-2.5 h-2.5'
                    )}
                    style={{
                      backgroundColor: color,
                      boxShadow: isHovered ? `0 0 0 2px white, 0 0 0 4px ${color}` : undefined,
                    }}
                  />
                  <div className="flex-1 min-w-0">
                    <div className={cn(
                      'text-sm truncate transition-colors duration-150',
                      isSelected && 'text-primary font-medium',
                      isHovered && !isSelected && 'font-medium'
                    )}>
                      {cluster.label || `Cluster ${cluster.cluster_num}`}
                    </div>
                    <div className="text-xs text-muted-foreground">
                      {cluster.decision_count.toLocaleString()} decisions
                    </div>
                  </div>
                </div>

                {/* Cluster stats when selected */}
                {isSelected && cluster.outcome_distribution && (
                  <div className="mt-1.5 ml-5 space-y-0.5 text-xs">
                    {Object.entries(cluster.outcome_distribution).map(([outcome, count]) => (
                      <div
                        key={outcome}
                        className="flex items-center justify-between text-muted-foreground"
                      >
                        <span className="capitalize">{outcome.replace('_', ' ')}</span>
                        <span>{count}</span>
                      </div>
                    ))}
                    {cluster.avg_compensation && (
                      <div className="flex items-center justify-between pt-0.5">
                        <span className="text-muted-foreground">Avg award</span>
                        <span className="text-emerald-600 font-medium">
                          £{Math.round(cluster.avg_compensation).toLocaleString()}
                        </span>
                      </div>
                    )}
                  </div>
                )}
              </button>
            );
          })}

          {/* Noise points */}
          <button
            onClick={() => onClusterClick?.(-1)}
            onMouseEnter={() => onClusterHover?.(-1)}
            onMouseLeave={() => onClusterHover?.(null)}
            className={cn(
              'w-full text-left px-2 py-1.5 rounded-md text-sm transition-all duration-150',
              selectedClusterId === -1
                ? 'bg-primary/10 text-primary font-medium'
                : 'hover:bg-muted/50',
              hoveredClusterId !== null && hoveredClusterId !== -1 && 'opacity-40'
            )}
          >
            <div className="flex items-center gap-2.5">
              <div className="w-2.5 h-2.5 rounded-full bg-gray-300" />
              <span className="text-muted-foreground">Unclustered</span>
            </div>
          </button>
        </div>
      </ScrollArea>
    </div>
  );
};

export default FOSClusterLegend;
